import apiBaseUrl from '../ApiBaseUrl';

// Get auth token
const getAuthToken = () => {
    return localStorage.getItem('authToken')?.toString();
};

// Dashboard Statistics Interface
export interface DashboardStats {
    totalUsers: number;
    totalTrades: number;
    totalDeposits: number;
    totalWithdrawals: number;
    totalBalance: number;
    totalLockedBalance: number;
    activeTrades: number;
    completedTrades: number;
    todayTrades: number;
    todayDeposits: number;
    todayWithdrawals: number;
    todayVolume: number;
    totalProfit: number;
    totalLoss: number;
}

// Recent Trade Interface
export interface RecentTrade {
    tradeId: number;
    userName: string;
    email: string;
    digitalCoinName: string;
    tradingAmount: number;
    status: string;
    tradingDateTimeUTC: string;
    tradeDuration: number;
    expectedProfit: number;
    tradeResult: string | null;
}

// Recent Activity Interface
export interface RecentActivity {
    activityType: string;
    userName: string;
    email: string;
    amount: number;
    digitalCoinName: string;
    activityDateTime: string;
    status: string;
    description: string;
}

// Chart Data Interfaces
export interface ChartPoint {
    label: string;
    value: number;
    date: string;
}

export interface ChartData {
    tradeVolumeData: ChartPoint[];
    userGrowthData: ChartPoint[];
    profitLossData: ChartPoint[];
    depositWithdrawalData: ChartPoint[];
}

// User Stats Interface
export interface UserStats {
    totalUsers: number;
    activeUsers: number;
    newUsersToday: number;
    newUsersThisWeek: number;
    newUsersThisMonth: number;
}

// Trading Stats Interface
export interface TradingStats {
    totalTrades: number;
    todayTrades: number;
    thisWeekTrades: number;
    thisMonthTrades: number;
    totalVolume: number;
    todayVolume: number;
    thisWeekVolume: number;
    thisMonthVolume: number;
    winRate: number;
    averageTradeAmount: number;
}

// Dashboard Summary Interface
export interface DashboardSummary {
    statistics: DashboardStats;
    recentTrades: RecentTrade[];
    recentActivities: RecentActivity[];
    chartData: ChartData;
    userStats: UserStats;
    tradingStats: TradingStats;
}

// Fetch dashboard statistics
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/stats`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch dashboard stats: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        throw error;
    }
};

// Fetch recent trades
export const fetchRecentTrades = async (count: number = 10): Promise<RecentTrade[]> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/recent-trades?count=${count}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch recent trades: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching recent trades:', error);
        throw error;
    }
};

// Fetch recent activities
export const fetchRecentActivities = async (count: number = 15): Promise<RecentActivity[]> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/recent-activities?count=${count}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch recent activities: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching recent activities:', error);
        throw error;
    }
};

// Fetch chart data
export const fetchChartData = async (days: number = 30): Promise<ChartData> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/chart-data?days=${days}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch chart data: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching chart data:', error);
        throw error;
    }
};

// Fetch user statistics
export const fetchUserStats = async (): Promise<UserStats> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/user-stats`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch user stats: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching user stats:', error);
        throw error;
    }
};

// Fetch trading statistics
export const fetchTradingStats = async (): Promise<TradingStats> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/trading-stats`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch trading stats: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching trading stats:', error);
        throw error;
    }
};

// Fetch complete dashboard summary
export const fetchDashboardSummary = async (): Promise<DashboardSummary> => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Dashboard/summary`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch dashboard summary: ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching dashboard summary:', error);
        throw error;
    }
};

// Helper function to format currency
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

// Helper function to format numbers with K, M, B suffixes
export const formatNumber = (num: number): string => {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

// Helper function to calculate percentage change
export const calculatePercentageChange = (current: number, previous: number): string => {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const change = ((current - previous) / previous) * 100;
    return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
};

// Helper function to get activity icon
export const getActivityIcon = (activityType: string) => {
    switch (activityType.toLowerCase()) {
        case 'deposit':
            return '💰';
        case 'withdrawal':
            return '💸';
        case 'trade':
            return '📈';
        case 'user':
            return '👤';
        default:
            return '📋';
    }
};

// Helper function to get status color
export const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case 'approved':
        case 'completed':
        case 'win':
            return '#48bb78';
        case 'pending':
        case 'active':
            return '#ed8936';
        case 'rejected':
        case 'cancelled':
        case 'loss':
            return '#f56565';
        default:
            return '#718096';
    }
};
